import PropTypes from 'prop-types'
import Iconify from 'src/components/iconify'
import { TreeItem, treeItemClasses, TreeView } from '@mui/x-tree-view'
import { Card, Checkbox, Stack } from '@mui/material'
import { useState } from 'react'
import Scrollbar from 'src/components/scrollbar'

const Folder = ({ onClick, isSelectable, selected, id, isExpanded, hasChildren }) => (
  <Stack direction='row' alignItems='center' height={35}>
    {isSelectable && (
      <Checkbox
        checked={selected.indexOf(id) >= 0}
        onChange={isSelectable ? onClick : undefined}
        onClick={e => (e.stopPropagation())}
      />
    )}
    {hasChildren && (
      <Iconify
        icon={isExpanded ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
        width={16}
        sx={{ mr: 1 }}
      />
    )}
    <Iconify icon='fluent-emoji-flat:file-folder' width={16} sx={{ ml: hasChildren ? 0 : 2 }}/>
  </Stack>
)

Folder.propTypes = {
  onClick: PropTypes.func.isRequired,
  isSelectable: PropTypes.bool.isRequired,
  selected: PropTypes.array.isRequired,
  id: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  hasChildren: PropTypes.bool.isRequired,
}

const File = ({ onClick, isSelectable, selected, id }) => (
  <Stack direction='row' alignItems='center'>
    <Checkbox
      style={{ visibility: isSelectable ? 'visible' : 'hidden' }}
      checked={selected.indexOf(id) >= 0}
      onChange={isSelectable ? onClick : undefined}
    />
    <Iconify icon='flat-color-icons:file' width={16} />
  </Stack>
)

File.propTypes = {
  onClick: PropTypes.func.isRequired,
  isSelectable: PropTypes.bool.isRequired,
  selected: PropTypes.array.isRequired,
  id: PropTypes.string.isRequired,
}

export default function Tree({ nodeList, multiSelect, selectionClick, selectedFolder, setSelectedFolder, selectedFile, setSelectedFile }) {
  const [expanded, setExpanded] = useState([])

  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds)
  }

  const getNodes = (arr) =>
    arr.map((elem, index) => {
      const isSelectable = elem.selectable

      const selectedFolderCloned = [...selectedFolder]

      const selectedFileCloned = [...selectedFile]

      const props = {
        onClick: (e) => {
          e.stopPropagation()
      
          const isChecked = e.target.checked

          const [newFolders, newFiles] = selectionClick(selectedFolderCloned, selectedFileCloned, isChecked, isSelectable, elem, multiSelect)

          if(newFolders){
            setSelectedFolder(newFolders)
          }

          if(newFiles){
            setSelectedFile(newFiles)
          }
        },
        style: { cursor: isSelectable ? 'pointer' : 'not-allowed' },
      }

      const hasChildren = elem.children && elem.children.length > 0

      const sortedChildren = hasChildren
        ? [...elem.children].sort((a, b) => {
            const isAXml = a.id.includes('.xml')
            const isBXml = b.id.includes('.xml')

            if (isAXml && !isBXml) return -1
            if (!isAXml && isBXml) return 1
            return 0
          })
        : []
      return (
        <TreeItem
          sx={{
            [`& .${treeItemClasses.label}`]: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            [`& .${treeItemClasses.root}`]: {
              backgroundColor: 'transparent'
            }
          }}
          key={index}
          nodeId={elem.id}
          label={elem.name}
          icon={
            elem.type === 'folder' ? (
              <Folder
                {...props}
                isSelectable={isSelectable}
                selected={selectedFolder}
                id={elem.id}
                isExpanded={expanded.includes(elem.id)}
                hasChildren={hasChildren}
              />
            ) : (
              <File {...props} isSelectable={isSelectable} selected={selectedFile} id={elem.id} />
            )
          }
        >
          {hasChildren ? getNodes(sortedChildren) : ''}
        </TreeItem>
      )
    })

  return (
    <Card sx={{ p: 2, backgroundColor:'action.hover', minHeight:'100%' }}>
      <Scrollbar sx={{ maxWidth: '100%' }}>
        <TreeView expanded={expanded} onNodeToggle={handleToggle}>
          {getNodes(nodeList)}
        </TreeView>
      </Scrollbar>
    </Card>
  )
}

Tree.propTypes = {
  nodeList: PropTypes.array,
  multiSelect: PropTypes.object,
  selectionClick: PropTypes.func,
  selectedFolder: PropTypes.array,
  setSelectedFolder: PropTypes.func,
  selectedFile: PropTypes.array,
  setSelectedFile: PropTypes.func,
}
